import { useEffect, useState } from "react";
import { supAPI, fmtINR } from "./supApi";
import { toast } from "sonner";
import { Plus, X, Save, ArrowRightLeft } from "lucide-react";
import { EmptyState } from "./SupplyItems";
import { NumberInput } from "@/components/ui/number-input";

export default function SupplyStock() {
  const [snap, setSnap] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [adjusting, setAdjusting] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const load = () => {
    supAPI.stockSnapshot({ store_id: storeFilter || undefined }).then(setSnap).catch(() => {});
  };
  useEffect(() => { load(); }, [storeFilter]); // eslint-disable-line
  useEffect(() => {
    supAPI.stores().then(setStores);
    supAPI.items().then(setItems);
  }, []);

  return (
    <div className="space-y-4" data-testid="sup-stock">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}
                data-testid="sup-stock-store-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All stores</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={() => setAdjusting(true)} disabled={!stores.length || !items.length}
                data-testid="sup-stock-adjust-open"
                className="ml-auto h-10 px-4 rounded-lg border border-slate-200 text-[13px] hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4"/> Adjust Stock
        </button>
        <button onClick={() => setTransferring(true)} disabled={stores.length < 2 || !items.length}
                data-testid="sup-stock-transfer-open"
                className="h-10 px-4 rounded-lg border border-slate-200 text-[13px] hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50">
          <ArrowRightLeft className="w-4 h-4"/> Transfer
        </button>
      </div>

      {!snap.length ? <EmptyState msg="No stock on hand yet. Adjust opening balance, or receive against a PO."/> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Store</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">On Hand</th>
                <th className="text-right px-4 py-3">Avg. Cost</th>
                <th className="text-right px-4 py-3">Value</th>
                <th className="text-center px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {snap.map(r => (
                <tr key={r.id} data-testid={`sup-stock-row-${r.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-medium text-hocp-navy">{r.store_name}</div>
                    <div className="text-[11.5px] mono text-slate-400">{r.store_code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[13px] text-slate-800">{r.item_name}</div>
                    <div className="text-[11.5px] mono text-slate-400">{r.item_sku}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{r.qty_on_hand} {r.uom}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtINR(r.avg_cost)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-hocp-navy">{fmtINR(r.value)}</td>
                  <td className="px-4 py-3 text-center">
                    {r.low_stock
                      ? <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 text-[11px] font-medium">Low</span>
                      : <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-medium">OK</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjusting && <AdjustDialog items={items} stores={stores}
                                   onClose={() => setAdjusting(false)}
                                   onDone={() => { setAdjusting(false); load(); }}/>}
      {transferring && <TransferDialog items={items} stores={stores}
                                        onClose={() => setTransferring(false)}
                                        onDone={() => { setTransferring(false); load(); }}/>}
    </div>
  );
}

function AdjustDialog({ items, stores, onClose, onDone }) {
  const [f, setF] = useState({ item_id: items[0]?.id || "", store_id: stores[0]?.id || "",
                                 qty_delta: 0, unit_cost: 0, reason: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.item_id || !f.store_id || !f.reason || Number(f.qty_delta) === 0) {
      toast.error("Item, store, non-zero qty, and reason required"); return;
    }
    setSaving(true);
    try {
      await supAPI.stockAdjust({ ...f, qty_delta: Number(f.qty_delta), unit_cost: Number(f.unit_cost) });
      toast.success("Stock adjusted"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Adjust Stock" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Item"><select value={f.item_id} onChange={e => setF({...f, item_id: e.target.value})} data-testid="adj-item" className={cls}>
          {items.map(i => <option key={i.id} value={i.id}>{i.sku} — {i.name}</option>)}
        </select></Fld>
        <Fld label="Store"><select value={f.store_id} onChange={e => setF({...f, store_id: e.target.value})} data-testid="adj-store" className={cls}>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></Fld>
        <Fld label="Qty (+ to add, − to remove)"><NumberInput value={f.qty_delta} onChange={e => setF({...f, qty_delta: e.target.value})} data-testid="adj-qty" className={cls}/></Fld>
        <Fld label="Unit Cost (₹) — for opening/receipt only"><NumberInput value={f.unit_cost} onChange={e => setF({...f, unit_cost: e.target.value})} className={cls}/></Fld>
        <Fld label="Reason" full><input value={f.reason} onChange={e => setF({...f, reason: e.target.value})} data-testid="adj-reason" placeholder="e.g. Opening balance, Damaged, Stock take" className={cls}/></Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="adj-submit"/>
    </Modal>
  );
}

function TransferDialog({ items, stores, onClose, onDone }) {
  const [f, setF] = useState({ item_id: items[0]?.id || "", from_store_id: stores[0]?.id || "",
                                 to_store_id: stores[1]?.id || "", qty: 0, reason: "Stock transfer" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (f.from_store_id === f.to_store_id) { toast.error("Source and destination must differ"); return; }
    if (Number(f.qty) <= 0) { toast.error("Qty must be positive"); return; }
    setSaving(true);
    try { await supAPI.stockTransfer({ ...f, qty: Number(f.qty) }); toast.success("Stock transferred"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Transfer Stock" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Item" full><select value={f.item_id} onChange={e => setF({...f, item_id: e.target.value})} data-testid="tr-item" className={cls}>
          {items.map(i => <option key={i.id} value={i.id}>{i.sku} — {i.name}</option>)}
        </select></Fld>
        <Fld label="From"><select value={f.from_store_id} onChange={e => setF({...f, from_store_id: e.target.value})} data-testid="tr-from" className={cls}>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></Fld>
        <Fld label="To"><select value={f.to_store_id} onChange={e => setF({...f, to_store_id: e.target.value})} data-testid="tr-to" className={cls}>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></Fld>
        <Fld label="Qty"><NumberInput value={f.qty} onChange={e => setF({...f, qty: e.target.value})} data-testid="tr-qty" className={cls}/></Fld>
        <Fld label="Reason"><input value={f.reason} onChange={e => setF({...f, reason: e.target.value})} className={cls}/></Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="tr-submit"/>
    </Modal>
  );
}

// ── Local UI primitives ────────────────────────────────────────────────
export const cls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
export function Fld({ label, children, full, required, optional, error }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <div className="text-[11.5px] text-slate-500 mb-1 font-medium">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
        {optional && <span className="text-slate-400 font-normal ml-1">(optional)</span>}
      </div>
      {children}
      {error && <p className="text-[11px] text-rose-500 mt-1">{error}</p>}
    </label>
  );
}
export function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
export function Footer({ onClose, onSubmit, saving, testid }) {
  return (
    <div className="pt-4 mt-4 border-t border-slate-100 flex justify-end gap-2 -mx-5 -mb-5 px-5 py-3 bg-slate-50">
      <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
      <button onClick={onSubmit} disabled={saving} data-testid={testid}
              className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium disabled:opacity-60 inline-flex items-center gap-2">
        <Save className="w-4 h-4"/> {saving ? "Saving…" : "Confirm"}
      </button>
    </div>
  );
}
